import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../designSystem/Button';
import DialogTitle from '../../designSystem/DialogTitle';
import { errorHandler } from '../../functions/errors';
import { showAlert } from '../../functions/utils';

interface DeleteItemResponse {
  displayMessage?: string;
}

type ActionType = 'delete' | 'reverse';

interface WarningDeleteDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  warningMessages?: string[];
  deleteItem: (dispatch: Dispatch) => Promise<DeleteItemResponse | void>;
  onDeleteSuccess?: () => void;
  confirmationText?: string;
  requireConfirmation?: boolean;
  children?: React.ReactNode;
  successMessage?: string;
  icon?: React.ReactNode;
  actionType?: ActionType;
}

const WarningDeleteDialog: React.FC<WarningDeleteDialogProps> = ({
  open,
  setOpen,
  title,
  warningMessages = [],
  deleteItem,
  onDeleteSuccess,
  confirmationText = 'Confirmo que entiendo las consecuencias de esta acción',
  requireConfirmation = true,
  children,
  successMessage,
  icon,
  actionType = 'delete',
}) => {
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      setAgreeTerms(false);
    }
  }, [open]);

  const getActionConfig = () => {
    switch (actionType) {
      case 'reverse':
        return {
          buttonText: 'Reversar',
          buttonIcon: <UndoIcon />,
          defaultSuccessMessage: 'La reversión se ha completado exitosamente.',
          dependenciesText: 'Se revertirán todas las dependencias',
        };
      case 'delete':
      default:
        return {
          buttonText: 'Eliminar',
          buttonIcon: <DeleteIcon />,
          defaultSuccessMessage: 'El elemento ha sido eliminado exitosamente.',
          dependenciesText: 'Se eliminarán todas las dependencias',
        };
    }
  };

  const actionConfig = getActionConfig();

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await deleteItem(dispatch);
      const message =
        response?.displayMessage ||
        successMessage ||
        actionConfig.defaultSuccessMessage;
      showAlert(dispatch, 'success', message);
      setOpen(false);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (value: boolean) => {
    if (!loading) {
      setOpen(value);
      if (!value) {
        setAgreeTerms(false);
      }
    }
  };

  const handleCancel = () => {
    if (!loading) {
      setOpen(false);
      setAgreeTerms(false);
    }
  };

  const canDelete = !requireConfirmation || agreeTerms;

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleDialogClose}>
      <DialogTitle setOpen={handleDialogClose}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {icon || <WarningAmberIcon color="warning" fontSize="medium" />}
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {warningMessages.length > 0 && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'warning.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="warning.dark"
                sx={{ mb: 0.5 }}
              >
                {actionConfig.dependenciesText}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Esta acción no se puede deshacer
              </Typography>
              <Stack spacing={0.5}>
                {warningMessages.map((message, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                    component="div"
                  >
                    - {message}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {children}

          {requireConfirmation && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'info.lighter',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.light',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setAgreeTerms(!agreeTerms)}
                    checked={agreeTerms}
                    sx={{ py: 0 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {confirmationText}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton
          onClick={handleCancel}
          color="secondary"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleDelete}
          color="error"
          disabled={!canDelete || loading}
          loading={loading}
          startIcon={actionConfig.buttonIcon}
        >
          {actionConfig.buttonText}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default WarningDeleteDialog;
